import {
  useCallback, useRef, useState,
} from 'react';
import { sanitizeHtml } from '@utils/strings/sanitizeHtml';
import { ChatAttachment } from '@components/features/candidate-prompt-bar/chatAttachment';

export type ChatHistoryItem = { id?: string; type: string; content: string };

interface UseChatComposerOptions {
  history?: ChatHistoryItem[]; // used for ArrowUp/ArrowDown to cycle human messages
  onSubmit: (text: string, attachment?: ChatAttachment | null) => Promise<void> | void; // your streaming call
}

export const useChatComposer = (options: UseChatComposerOptions) => {
  const { /* history = [], */ onSubmit } = options;
  const [input, setInput] = useState('');
  const [attachment, setAttachment] = useState<ChatAttachment | null>(null);
  // const [previousSentMessagesIndex, setPreviousSentMessagesIndex] = useState<number | null>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  /* const humanMessages = useMemo(
    () => history.filter((m) => m?.type === 'human'),
    [history],
  ); */

  const submit = useCallback(async () => {
    const value = input;
    // An attachment alone is a valid message, and goes out with empty text rather than an
    // invented caption — the turn still carries the file and its link.
    if (!sanitizeHtml(value) && !attachment) return;
    setInput('');
    setAttachment(null);
    // setPreviousSentMessagesIndex(null);
    await onSubmit(value, attachment);
  }, [input, attachment, onSubmit]);

  const clearAttachment = useCallback(() => setAttachment(null), []);

  const onChange = useCallback((e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setInput(e.target.value);
  }, []);

  const onEnter = useCallback((e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    e.preventDefault();
    void submit();
  }, [submit]);

  const onShiftEnter = useCallback((e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    e.preventDefault();
    setInput((prev) => `${prev}\n`);
    if (textareaRef.current) {
      textareaRef.current.scrollTop = textareaRef.current.scrollHeight;
    }
  }, []);

  /*  const onArrowUp = useCallback((e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    const textarea = e.currentTarget;
    const cursorPosition = textarea.selectionStart;
    const textBeforeCursor = textarea.value.substring(0, cursorPosition);
    const isAtFirstLine = !textBeforeCursor.includes('\n');

    // Only intercept if cursor is at the first line
    if (!isAtFirstLine) return;

    e.preventDefault();
    if (previousSentMessagesIndex === null && humanMessages.length) {
      setPreviousSentMessagesIndex(humanMessages.length - 1);
      setInput(humanMessages[humanMessages.length - 1].content);
    } else if (previousSentMessagesIndex !== null && previousSentMessagesIndex > 0) {
      setPreviousSentMessagesIndex(previousSentMessagesIndex - 1);
      setInput(humanMessages[previousSentMessagesIndex - 1].content);
    }
  }, [humanMessages, previousSentMessagesIndex]);

  const onArrowDown = useCallback((e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    const textarea = e.currentTarget;
    const cursorPosition = textarea.selectionStart;
    const textAfterCursor = textarea.value.substring(cursorPosition);
    const isAtLastLine = !textAfterCursor.includes('\n');

    // Only intercept if cursor is at the last line AND we're navigating history
    if (!isAtLastLine || previousSentMessagesIndex === null) return;

    e.preventDefault();
    if (previousSentMessagesIndex < humanMessages.length - 1) {
      setPreviousSentMessagesIndex(previousSentMessagesIndex + 1);
      setInput(humanMessages[previousSentMessagesIndex + 1].content);
    } else {
      setPreviousSentMessagesIndex(null);
      setInput('');
    }
  }, [humanMessages, previousSentMessagesIndex]); */

  const onKeyDown = useCallback((e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) return onEnter(e);
    if (e.key === 'Enter' && e.shiftKey) return onShiftEnter(e);
    // if (e.key === 'ArrowUp') return onArrowUp(e);
    // if (e.key === 'ArrowDown') return onArrowDown(e);
    return undefined;
  }, [onEnter, onShiftEnter]);
  // , onArrowUp, onArrowDown

  return {
    input,
    setInput,
    textareaRef,
    onChange,
    onKeyDown,
    submit,
    attachment,
    setAttachment,
    clearAttachment,
  };
};
